import React from 'react';
import { Box } from '@mui/material';
import {
  ActionButton,
  LogoMark,
  StatusPill,
  useModeTokens,
} from '@/common/components/ui';
import { useGoogleSignIn } from '@/common/auth/components/GoogleSignIn';
import { NAV } from './content';

/**
 * Public header. The BETA pill carries what the old rainbow title used to
 * shout — the product is still in beta, and a visitor should know that before
 * they sign in, not after.
 */
export const LandingHeader: React.FC<{ onGetTheApp: () => void }> = ({
  onGetTheApp,
}) => {
  const t = useModeTokens();
  const { signIn, isLoading, isConfigured } = useGoogleSignIn();

  return (
    <Box
      component="header"
      className="lp-gloss-header"
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: { xs: '12px', md: '26px' },
        padding: { xs: '16px 20px', sm: '18px 28px', md: '20px 40px' },
        background: t.surfaces.screen,
        borderBottom: `1px solid ${t.borders.hairline}`,
        boxShadow: t.shadows.cardFlat,
      }}
    >
      <LogoMark size={26} />
      <StatusPill size={9} sx={{ flex: 'none' }}>
        BETA
      </StatusPill>

      <Box
        component="nav"
        sx={{
          display: { xs: 'none', md: 'flex' },
          gap: '22px',
          fontSize: '13px',
          marginLeft: '14px',
        }}
      >
        {NAV.map(([label, id]) => (
          <Box
            component="a"
            href={`#${id}`}
            key={id}
            sx={{
              color: t.text.secondary,
              textDecoration: 'none',
              '&:hover': { color: t.text.primary },
            }}
          >
            {label}
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          marginLeft: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        {isConfigured && (
          <Box
            component="button"
            type="button"
            onClick={signIn}
            disabled={isLoading}
            sx={{
              background: 'none',
              border: 0,
              padding: 0,
              fontFamily: 'inherit',
              fontSize: '13px',
              whiteSpace: 'nowrap',
              color: t.text.secondary,
              cursor: 'pointer',
              '&:hover': { color: t.text.primary },
            }}
          >
            {isLoading ? (
              'Signing in…'
            ) : (
              <>
                Sign in
                {/* Named only where there's room — the hero and closing CTAs
                    carry the mechanism on narrow screens. */}
                <Box
                  component="span"
                  sx={{ display: { xs: 'none', md: 'inline' } }}
                >
                  {' '}
                  with Google
                </Box>
              </>
            )}
          </Box>
        )}
        <ActionButton className="lp-gloss-action" onClick={onGetTheApp}>
          Get the app
        </ActionButton>
      </Box>
    </Box>
  );
};

export default LandingHeader;
